import { useEffect, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SearchBox } from '@/components/form/SearchBox';
import { CustomSelect } from '@/components/form/CustomSelect';
import { SortableHeader } from '@/components/SortableHeader';
import { TileMenu } from '@/components/TileMenu';
import { useColumnSort } from '@/lib/tableSort';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useTooltip } from '@/hooks/useTooltip';
import { showToast } from '@/lib/toast';
import {
  fetchAdminUsers,
  fetchAdminUser,
  fetchAdminUserOptions,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  type AdminUserDetail,
  type AdminUserOptions,
  type AdminUserRow,
} from '@/api/adminUsers';
import type { ClientsPageMeta } from '@/api/clients';
import { AdminUserForm } from './AdminUserForm';
import { AdminCreateWizard } from './AdminCreateWizard';

type SortKey = 'name' | 'lastName' | 'firstName' | 'surname' | 'email' | 'status';
type FormMode =
  | { mode: 'create' }
  | { mode: 'view'; user: AdminUserDetail }
  | { mode: 'edit'; user: AdminUserDetail }
  | null;

/**
 * "Администраторы" — 1:1 старые Orchid UserListScreen/UserEditScreen
 * (см. докблок AdminUserController в Laravel). Таблица как у клиентов, в
 * конце строки три точки: просмотр, редактирование, удаление.
 *
 * Клик по строке открывает карточку на просмотр; перейти к правке можно
 * кнопкой сверху — так же, как в карточке клиента.
 */
export function AdminsPage() {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [status, setStatus] = useState('');
  const [roleId, setRoleId] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [meta, setMeta] = useState<ClientsPageMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<AdminUserOptions | null>(null);
  const [form, setForm] = useState<FormMode>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  /** Слот в шапке под кнопки правки — форма рисует их туда порталом. */
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);
  const { confirm, dialog } = useConfirmDialog();
  const removeTooltip = useTooltip<HTMLButtonElement>('Удалить администратора');

  const sort = useColumnSort<SortKey>();
  const sortParam = sort.key ? (sort.dir === 'desc' ? `-${sort.key}` : sort.key) : undefined;
  const reload = () => setReloadToken((t) => t + 1);
  const current = form && form.mode !== 'create' ? form.user : null;

  useEffect(() => {
    setPage(1);
  }, [search, status, roleId]);

  useEffect(() => {
    let cancelled = false;
    fetchAdminUserOptions()
      .then((data) => {
        if (!cancelled) setOptions(data);
      })
      .catch(() => {
        // Справочники не критичны: таблица работает и без фильтров.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (form) return;
    let cancelled = false;
    setRows(null);
    fetchAdminUsers({
      search: search || undefined,
      status: status || undefined,
      roleId: roleId || undefined,
      sort: sortParam,
      page,
    })
      .then(({ items, meta: pageMeta }) => {
        if (cancelled) return;
        setRows(items);
        setMeta(pageMeta);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить администраторов.');
      });
    return () => {
      cancelled = true;
    };
  }, [search, status, roleId, sortParam, page, reloadToken, form]);

  const open = async (id: number, mode: 'view' | 'edit') => {
    try {
      const user = await fetchAdminUser(id);
      setForm(mode === 'view' ? { mode: 'view', user } : { mode: 'edit', user });
    } catch {
      showToast('Не удалось открыть администратора.');
    }
  };

  const remove = async (target: { id: number; name: string | null }) => {
    const ok = await confirm(`Удалить администратора «${target.name}»? Это действие необратимо.`, {
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    setBusyId(target.id);
    try {
      await deleteAdminUser(target.id);
      showToast('Администратор удалён');
      setForm(null);
      reload();
    } catch {
      showToast('Не удалось удалить администратора.');
    } finally {
      setBusyId(null);
    }
  };

  const title =
    form?.mode === 'create'
      ? 'Новый администратор'
      : current
        ? (current.name ?? 'Администратор')
        : 'Администраторы';

  return (
    <section className="catalog">
      {/* Возврат к списку — стрелкой сверху, как в карточке клиента. */}
      {form && (
        <button type="button" className="detail__back" onClick={() => setForm(null)}>
          <Icon name="chevronLeft" size={14} /> Все администраторы
        </button>
      )}

      <header className="catalog__header catalog__header--row">
        <div>
          <h1>{title}</h1>
          <p className="catalog__lede">
            {form ? 'Профиль, единый вход, роли и права.' : 'Учётные записи администраторов.'}
          </p>
        </div>

        {!form && (
          <button type="button" className="btn btn--primary" onClick={() => setForm({ mode: 'create' })}>
            <Icon name="plus" size={15} /> Добавить администратора
          </button>
        )}
        {/* Карточка открывается на просмотр; правка и удаление — кнопками сверху. */}
        {form?.mode === 'view' && (
          <div className="catalog__header-actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setForm({ mode: 'edit', user: form.user })}
            >
              <Icon name="pencil" size={15} /> Редактировать
            </button>
            <button
              type="button"
              className="btn btn--icon-danger"
              aria-label="Удалить"
              disabled={busyId === form.user.id}
              onClick={() => remove(form.user)}
              {...removeTooltip.handlers}
              ref={removeTooltip.ref}
            >
              <Icon name="trash" size={17} />
              {removeTooltip.node}
            </button>
          </div>
        )}
        {/* Отмена/Сохранить правки — на месте кнопки «Редактировать»; сами кнопки
            рисует форма порталом в этот слот, состояние правки живёт в ней. */}
        {form?.mode === 'edit' && <div className="catalog__header-actions" ref={setHeaderSlot} />}
      </header>

      <div className="card">
        {form?.mode === 'create' && (
          <AdminCreateWizard
            options={options}
            onCancel={() => setForm(null)}
            // После создания открываем карточку нового администратора, а не список.
            onCreate={async (payload) => {
              const created = await createAdminUser(payload);
              setForm({ mode: 'view', user: created });
              showToast(`Администратор «${created.name}» создан`);
              reload();
              return created;
            }}
          />
        )}

        {current && (
          <AdminUserForm
            key={`${current.id}-${form?.mode}`}
            initial={current}
            options={options}
            readOnly={form?.mode === 'view'}
            actionsSlot={headerSlot}
            // Отмена правки возвращает к карточке этого же администратора, а не к списку.
            onCancel={() =>
              setForm(form?.mode === 'edit' ? { mode: 'view', user: current } : null)
            }
            // Сохранение оставляет пользователя там же, где он был: тот же режим
            // и та же вкладка (mode в key не меняется — форма не пересоздаётся).
            onSave={async (payload) => {
              const updated = await updateAdminUser(current.id, payload);
              setForm(form?.mode === 'edit' ? { mode: 'edit', user: updated } : { mode: 'view', user: updated });
              showToast(`Администратор «${updated.name}» обновлён`);
              reload();
            }}
          />
        )}

        {!form && (
          <>
            <div className="filter-row">
              <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск" />
              <div className="group-select">
                <CustomSelect
                  value={roleId}
                  options={[{ value: '', label: 'Все роли' }, ...(options?.roles ?? [])]}
                  onChange={setRoleId}
                />
              </div>
              <div className="group-select">
                <CustomSelect
                  value={status}
                  options={[{ value: '', label: 'Все статусы' }, ...(options?.statuses ?? [])]}
                  onChange={setStatus}
                />
              </div>
            </div>
            <p className="edit-field__hint filter-hint">Поиск по названию, ФИО или электронной почте.</p>

            {error && <p className="field-error">{error}</p>}

            {rows === null ? (
              <div className="panel-loading">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <p className="cell-muted">Администраторы не найдены.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <SortableHeader label="Название" sortKey="name" sort={sort} onSort={sort.toggle} />
                      <SortableHeader label="Фамилия" sortKey="lastName" sort={sort} onSort={sort.toggle} />
                      <SortableHeader label="Имя" sortKey="firstName" sort={sort} onSort={sort.toggle} />
                      <SortableHeader label="Отчество" sortKey="surname" sort={sort} onSort={sort.toggle} />
                      <SortableHeader label="Почта" sortKey="email" sort={sort} onSort={sort.toggle} />
                      <th>Роли</th>
                      <SortableHeader label="Статус" sortKey="status" sort={sort} onSort={sort.toggle} />
                      <th aria-label="Действия" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="table__row--clickable" onClick={() => open(row.id, 'view')}>
                        <td className="cell-primary">{row.name ?? '—'}</td>
                        <td>{row.lastName ?? '—'}</td>
                        <td>{row.firstName ?? '—'}</td>
                        <td>{row.surname ?? '—'}</td>
                        <td className="cell-muted">{row.email ?? '—'}</td>
                        <td>{row.roles.length > 0 ? row.roles.join(', ') : '—'}</td>
                        <td>
                          <span className={`badge ${row.status === 'active' ? 'badge--good' : 'badge--neutral'}`}>
                            {row.status === 'active' ? 'Активен' : 'Отключён'}
                          </span>
                        </td>
                        {/* stopPropagation: клик по меню не должен открывать карточку. */}
                        <td onClick={(e) => e.stopPropagation()}>
                          <TileMenu
                            disabled={busyId === row.id}
                            onView={() => open(row.id, 'view')}
                            onEdit={() => open(row.id, 'edit')}
                            onDelete={() => remove(row)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {meta && meta.lastPage > 1 && (
              <div className="table-footer">
                <span className="table-footer__status">
                  Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
                </span>
                <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {dialog}
    </section>
  );
}
